import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, PauseCircle, Plus, PlayCircle, Trash2 } from 'lucide-react';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { getCdnUrl } from '../../../config/cdn';
import { placeService, City, State } from '../../../services/api';
import { routesService, RouteListItem } from '../../../services/routes';
import { useDebounce } from '../../../hooks/useDebounce';

const MIN_SEARCH_LENGTH = 3;

const statusLabel: Record<string, string> = {
  Draft: 'Rascunho',
  Published: 'Publicado',
  Archived: 'Arquivado',
};

const routeTypeLabel: Record<string, string> = {
  Collection: 'Roteiro',
  Challenge: 'Desafio',
};

const normalizeText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const stateAbbreviationByName: Record<string, string> = {
  acre: 'AC',
  alagoas: 'AL',
  amapa: 'AP',
  amazonas: 'AM',
  bahia: 'BA',
  ceara: 'CE',
  'distrito federal': 'DF',
  'espirito santo': 'ES',
  goias: 'GO',
  maranhao: 'MA',
  'mato grosso': 'MT',
  'mato grosso do sul': 'MS',
  'minas gerais': 'MG',
  para: 'PA',
  paraiba: 'PB',
  parana: 'PR',
  pernambuco: 'PE',
  piaui: 'PI',
  'rio de janeiro': 'RJ',
  'rio grande do norte': 'RN',
  'rio grande do sul': 'RS',
  rondonia: 'RO',
  roraima: 'RR',
  'santa catarina': 'SC',
  'sao paulo': 'SP',
  sergipe: 'SE',
  tocantins: 'TO',
};

const RoutesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search, 400);
  const effectiveSearch = debouncedSearch.length >= MIN_SEARCH_LENGTH ? debouncedSearch : '';

  const { data: cities = [] } = useQuery({
    queryKey: ['route-list-cities'],
    queryFn: async () => {
      const response = await placeService.getCities();
      return response ?? [];
    },
  });

  const { data: states = [] } = useQuery({
    queryKey: ['route-list-states'],
    queryFn: async () => {
      const response = await placeService.getStates();
      return response ?? [];
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['admin-routes', page, pageSize, statusFilter, effectiveSearch],
    queryFn: () => routesService.getRoutes({
      page,
      pageSize,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      search: effectiveSearch || undefined,
    }),
  });

  const routes = data?.routes ?? [];
  const pagination = data?.pagination;

  const cityLabelById = useMemo(() => {
    return new Map(
      cities.map((city: City) => [
        city.id,
        `${city.name} - ${stateAbbreviationByName[normalizeText(city.stateName ?? '')] ?? city.stateName ?? 'Estado'}`,
      ])
    );
  }, [cities]);

  const stateLabelById = useMemo(() => {
    return new Map(states.map((state: State) => [state.id, state.name]));
  }, [states]);

  const publishMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'publish' | 'archive' }) =>
      status === 'publish' ? routesService.publishRoute(id) : routesService.archiveRoute(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-routes'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => routesService.deleteRoute(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-routes'] }),
  });

  const totalRoutes = useMemo(() => pagination?.totalCount ?? routes.length, [pagination, routes.length]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <button onClick={() => navigate('/places')} className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Roteiros</h1>
          <p className="text-gray-600">Gerencie roteiros editoriais e as cafeterias incluidas em cada um.</p>
        </div>
        <Link to="/routes/new" className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 inline-flex items-center">
          <Plus className="h-4 w-4 mr-2" />
          Novo Roteiro / Desafio
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow mb-6 p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Buscar por titulo ou slug..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
          />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
          >
            <option value="all">Todos os status</option>
            <option value="Draft">Rascunho</option>
            <option value="Published">Publicado</option>
            <option value="Archived">Arquivado</option>
          </select>
        </div>
        <p className="mt-3 text-sm text-gray-500">
          Para criar um desafio, clique em <strong>Novo Roteiro / Desafio</strong> e escolha <strong>Challenge</strong> no formulario.
        </p>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titulo</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scope</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cafes</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Atualizado</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acoes</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {routes.map((route: RouteListItem) => (
              <tr key={route.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <img src={getCdnUrl(route.coverImageUrl) ?? route.coverImageUrl} alt="" className="h-10 w-14 rounded-md object-cover" />
                    <div>
                      <div className="font-medium text-gray-900">{route.title}</div>
                      <div className="text-sm text-gray-500">{route.slug}</div>
                      {route.featured && <span className="inline-flex mt-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800">Featured</span>}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                    {routeTypeLabel[route.routeType] ?? route.routeType}
                  </span>
                  {route.routeType === 'Challenge' ? (
                    <div className="mt-2 text-xs text-gray-500">
                      {route.startDate ? new Date(route.startDate).toLocaleDateString('pt-BR') : 'Sem início'} até {route.endDate ? new Date(route.endDate).toLocaleDateString('pt-BR') : 'sem término'}
                    </div>
                  ) : null}
                </td>
                <td className="px-6 py-4">
                  <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                    {statusLabel[route.status] ?? route.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {route.scopeType} / {
                    route.scopeType === 'City'
                      ? cityLabelById.get(route.scopeKey) ?? route.scopeKey
                      : route.scopeType === 'State'
                        ? stateLabelById.get(route.scopeKey) ?? route.scopeKey
                        : route.scopeKey
                  }
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">{route.coffeeShopCount}</td>
                <td className="px-6 py-4 text-sm text-gray-700">{new Date(route.updatedAt).toLocaleDateString('pt-BR')}</td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-2">
                    <Link to={`/routes/${route.id}/edit`} className="text-indigo-600 hover:text-indigo-900" title="Editar">
                      <Edit className="h-4 w-4" />
                    </Link>
                    <button
                      onClick={() => publishMutation.mutate({ id: route.id, status: route.status === 'Published' ? 'archive' : 'publish' })}
                      className="text-emerald-600 hover:text-emerald-900"
                      title={route.status === 'Published' ? 'Arquivar' : 'Publicar'}
                    >
                      {route.status === 'Published' ? <PauseCircle className="h-4 w-4" /> : <PlayCircle className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Excluir roteiro "${route.title}"?`)) {
                          deleteMutation.mutate(route.id);
                        }
                      }}
                      className="text-red-600 hover:text-red-900"
                      title="Excluir"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {pagination && pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between text-sm text-gray-700">
            <div>
              Mostrando {routes.length} de {totalRoutes} roteiros
            </div>
            <div className="flex gap-2">
              <button disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-3 py-2 rounded-lg border disabled:opacity-50">Anterior</button>
              <button disabled={page === pagination.totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-2 rounded-lg border disabled:opacity-50">Próximo</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoutesPage;
